import api from './api';
import { CostFormValues, CostType } from '../types/cost.types';

const costService = {
  // Obter todos os custos com filtros
  async getCosts(params?: {
    serviceRequest?: string;
    commercialVisit?: string;
    costType?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    populate?: string | string[];
  }) {
    const response = await api.get('/costs', { params });
    return response.data;
  },

  // Obter um custo específico por ID
  async getCostById(id: string) {
    const response = await api.get(`/costs/${id}`);
    return response.data.cost;
  },

  // Criar um novo custo
  async createCost(costData: CostFormValues) {
    const response = await api.post('/costs', costData);
    return response.data;
  },

  // Atualizar um custo existente
  async updateCost(id: string, costData: Partial<CostFormValues>) {
    const response = await api.put(`/costs/${id}`, costData);
    return response.data;
  },

  // Excluir um custo (soft delete)
  async deleteCost(id: string) {
    const response = await api.delete(`/costs/${id}`);
    return response.data;
  },

  // Obter resumo de custos
  async getCostsSummary(params?: {
    serviceRequest?: string;
    commercialVisit?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const response = await api.get('/costs/summary', { params });
    return response.data;
  },

  // Obter custos por chamado técnico
  async getCostsByServiceRequest(serviceRequestId: string) {
    const response = await api.get(`/costs/service-request/${serviceRequestId}`);
    return response.data;
  },

  // Obter custos por visita comercial
  async getCostsByCommercialVisit(visitId: string): Promise<{ costs: CostType[]; totalAmount: number }> {
    const response = await api.get(`/costs`, { params: { commercialVisit: visitId } });
    return response.data;
  },

  // Calcular custo baseado em taxa horária e horas trabalhadas
  calculateHourlyRateCost(hourlyRate: number, hoursWorked: number): number {
    return hourlyRate * hoursWorked;
  },

  // Calcular custo baseado em quilômetros e custo por quilômetro
  calculateKilometerCost(kilometers: number, costPerKilometer: number): number {
    return kilometers * costPerKilometer;
  },

  // Calcular custo baseado em quantidade e custo unitário
  calculateQuantityCost(quantity: number, unitCost: number): number {
    return quantity * unitCost;
  },

  // Formatar valor monetário
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  },
  
  // Obter custo médio por tipo de custo (usando endpoint existente)
  async getCostAverageByType() {
    // Buscar todos os custos e calcular médias no frontend
    const response = await api.get('/costs', { 
      params: { 
        limit: 1000, // Buscar um número razoável de custos
        isActive: true 
      } 
    });
    return {
      data: this.calculateAverageByType(response.data.costs || [])
    };
  },
  
  // Obter custo médio por técnico (usando endpoint existente)
  async getCostAverageByTechnician() {
    // Buscar custos técnicos com chamados populados
    const response = await api.get('/costs', { 
      params: { 
        costType: 'technical_direct',
        limit: 1000,
        isActive: true,
        populate: ['serviceRequest', 'serviceRequest.assignedTo']
      } 
    });
    return {
      data: this.calculateAverageByTechnician(response.data.costs || [])
    };
  },
  
  // Obter custo médio por vendedor (usando endpoint existente)
  async getCostAverageBySalesPerson() {
    try {
      console.log('Buscando dados para cálculo de custo médio por vendedor...');
      
      // Buscar todos os custos que tenham uma visita comercial associada
      const response = await api.get('/costs', { 
        params: { 
          limit: 1000,
          isActive: true,
          // Especificar que queremos apenas custos com visita comercial
          commercialVisit: { $exists: true },
          // Garantir que os dados da visita comercial e do vendedor sejam populados
          populate: ['commercialVisit', 'commercialVisit.salesPerson']
        } 
      });
      
      // Verificar se os dados foram recebidos corretamente
      const costs = response.data.costs || [];
      console.log('Custos recebidos para cálculo de média por vendedor:', costs.length);
      
      // Verificar se as visitas comerciais estão sendo populadas corretamente
      const costsWithCommercialVisit = costs.filter((cost: any) => cost.commercialVisit);
      console.log('Custos com visita comercial populada:', costsWithCommercialVisit.length);
      
      // Verificar se os vendedores estão sendo populados corretamente
      const costsWithSalesPerson = costsWithCommercialVisit.filter(
        (cost: any) => cost.commercialVisit.salesPerson
      );
      console.log('Custos com vendedor populado:', costsWithSalesPerson.length);
      
      // Calcular médias por vendedor
      const result = this.calculateAverageBySalesPerson(costs);
      console.log('Vendedores encontrados após cálculo:', result.length);
      
      return {
        data: result
      };
    } catch (error) {
      console.error('Erro ao buscar custos para cálculo de média por vendedor:', error);
      return { data: [] };
    }
  },
  
  // Obter custo médio por categoria (usando endpoint existente)
  async getCostAverageByCategory() {
    // Buscar todos os custos e calcular médias no frontend
    const response = await api.get('/costs', { 
      params: { 
        limit: 1000,
        isActive: true 
      } 
    });
    return {
      data: this.calculateAverageByCategory(response.data.costs || [])
    };
  },

  // Funções auxiliares para cálculo de médias
  calculateAverageByType(costs: any[]): Array<{name: string; totalAmount: number; count: number; averageCost: number}> {
    interface TypeGroup {
      totalAmount: number;
      count: number;
    }
    
    const typeGroups: Record<string, TypeGroup> = {};
    
    costs.forEach((cost: any) => {
      if (!cost.costType) return;
      
      if (!typeGroups[cost.costType]) {
        typeGroups[cost.costType] = { totalAmount: 0, count: 0 };
      }
      
      typeGroups[cost.costType].totalAmount += cost.amount || 0;
      typeGroups[cost.costType].count += 1;
    });
    
    return Object.entries(typeGroups).map(([type, data]: [string, TypeGroup]) => ({
      name: type,
      totalAmount: data.totalAmount,
      count: data.count,
      averageCost: data.count > 0 ? data.totalAmount / data.count : 0
    }));
  },
  
  calculateAverageByTechnician(costs: any[]): Array<{name: string; totalAmount: number; count: number; averageCost: number}> {
    interface TechnicianGroup {
      totalAmount: number;
      count: number;
      name: string;
    }
    
    const technicianGroups: Record<string, TechnicianGroup> = {};
    
    costs.forEach((cost: any) => {
      if (!cost.serviceRequest || !cost.serviceRequest.assignedTo) return;
      
      const serviceRequest = cost.serviceRequest;
      const technician = serviceRequest.assignedTo;
      const techId = typeof technician === 'string' ? technician : technician._id;
      const techName = typeof technician === 'string' ? 'Técnico' : 
                      (technician.displayName || technician.name || 'Técnico');
      
      if (!technicianGroups[techId]) {
        technicianGroups[techId] = { totalAmount: 0, count: 0, name: techName };
      }
      
      technicianGroups[techId].totalAmount += cost.amount || 0;
      technicianGroups[techId].count += 1;
    });
    
    return Object.values(technicianGroups)
      .map((data: TechnicianGroup) => ({
        name: data.name,
        totalAmount: data.totalAmount,
        count: data.count,
        averageCost: data.count > 0 ? data.totalAmount / data.count : 0
      }))
      .sort((a, b) => b.averageCost - a.averageCost); // Ordenar do maior para o menor
  },
  
  calculateAverageBySalesPerson(costs: any[]): Array<{name: string; totalAmount: number; count: number; averageCost: number}> {
    interface SalesPersonGroup {
      totalAmount: number;
      count: number;
      name: string;
    }
    
    const salesPersonGroups: Record<string, SalesPersonGroup> = {};
    let totalCostsProcessed = 0;
    let costsWithVisit = 0;
    let costsWithSalesPerson = 0;
    
    costs.forEach((cost: any) => {
      totalCostsProcessed++;
      
      // Verificar se o custo tem uma visita comercial associada
      if (!cost.commercialVisit) return;
      costsWithVisit++;
      
      // Verificar se a visita comercial tem um vendedor responsável
      if (!cost.commercialVisit.salesPerson) return;
      costsWithSalesPerson++;
      
      const commercialVisit = cost.commercialVisit;
      const salesPerson = commercialVisit.salesPerson;
      
      // Extrair ID e nome do vendedor
      const salesId = typeof salesPerson === 'string' ? salesPerson : salesPerson._id;
      const salesName = typeof salesPerson === 'string' ? 'Vendedor' : 
                      (salesPerson.displayName || salesPerson.name || 'Vendedor');
      
      // Inicializar grupo para este vendedor se ainda não existir
      if (!salesPersonGroups[salesId]) {
        salesPersonGroups[salesId] = { totalAmount: 0, count: 0, name: salesName };
      }
      
      // Acumular valor e contagem para cálculo da média
      salesPersonGroups[salesId].totalAmount += cost.amount || 0;
      salesPersonGroups[salesId].count += 1;
    });
    
    // Log para debug
    console.log(`Processamento de custos por vendedor:\n - Total de custos: ${totalCostsProcessed}\n - Custos com visita comercial: ${costsWithVisit}\n - Custos com vendedor identificado: ${costsWithSalesPerson}`);
    console.log('Vendedores encontrados:', Object.keys(salesPersonGroups).length);
    
    // Calcular médias e transformar em array ordenado
    return Object.values(salesPersonGroups)
      .map((data: SalesPersonGroup) => ({
        name: data.name,
        totalAmount: data.totalAmount,
        count: data.count,
        averageCost: data.count > 0 ? data.totalAmount / data.count : 0
      }))
      .sort((a, b) => b.averageCost - a.averageCost);
  },
  
  calculateAverageByCategory(costs: any[]): Array<{name: string; totalAmount: number; count: number; averageCost: number}> {
    interface CategoryGroup {
      totalAmount: number;
      count: number;
    }
    
    const categoryGroups: Record<string, CategoryGroup> = {};
    
    costs.forEach((cost: any) => {
      if (!cost.category) return;
      
      if (!categoryGroups[cost.category]) {
        categoryGroups[cost.category] = { totalAmount: 0, count: 0 };
      }
      
      categoryGroups[cost.category].totalAmount += cost.amount || 0;
      categoryGroups[cost.category].count += 1;
    });
    
    return Object.entries(categoryGroups).map(([category, data]: [string, CategoryGroup]) => ({
      name: category,
      totalAmount: data.totalAmount,
      count: data.count,
      averageCost: data.count > 0 ? data.totalAmount / data.count : 0
    }));
  }
};

export default costService;
